import { BulkPermissionsResult, PermissionDescriptor } from '../../types';
import { FISRT_LEVEL_PERMISSION_INFO, OperationStatusInfo } from '../UserProfileEditor/utils';
import { NEXA_PERMISSION, API_MANAGEMENT_PERMISSION } from '@src/utils/constants';
import { MAX_ACCOUNTS_COUNT_TO_OPERATE, MAX_USER_COUNT_TO_OPERATE } from '../utils';

/** `count === 1 ? singular : plural` — used across the bulk-editor's user/account count strings. */
export function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

/**
 * The server caps a By-User bulk op on the TOTAL resolved profiles across all selected accounts —
 * `MAX_USER_COUNT_TO_OPERATE` (50) — not on the account count alone (bulkPermissions.ts sums each
 * account's userIds). Since this editor applies the SAME selected users to EVERY chosen account, the
 * effective total is `users × accounts`, so the real ceiling on accounts is `floor(50 / users)`. The
 * static "Max 50 accounts" label doesn't reflect that, so compute the true per-selection max here and
 * surface it in the UI (otherwise the operator only discovers the cap when the server rejects submit).
 *
 * Guards: at least 1 user (avoid divide-by-zero → treat 0/empty as a single user). Returns **0** when
 * the selection already exceeds the total cap (> 50 users) — no account count is valid then, so the
 * UI blocks account selection rather than advertising a "1 account" option the server would reject.
 */
export function maxAccountsForUserCount(userCount: number): number {
  const users = Math.max(1, Math.floor(userCount) || 0);
  return Math.floor(MAX_USER_COUNT_TO_OPERATE / users);
}

/**
 * Validate an accounts selection against BOTH caps the server enforces — the account-count cap and
 * the total (users × accounts) profile cap — so the operator sees the real limit BEFORE submitting
 * (the server would otherwise reject a valid-looking selection). Pure + framework-free so it's unit
 * testable and reused by the antd form validator. Returns `{ ok }` or `{ ok: false, message }`.
 */
export function validateBulkAccounts(params: { accountCount: number; userCount: number }): {
  ok: boolean;
  message?: string;
} {
  const { accountCount, userCount } = params;
  if (accountCount <= 0) {
    return { ok: false, message: 'Please select at least one account' };
  }
  if (accountCount > MAX_ACCOUNTS_COUNT_TO_OPERATE) {
    return {
      ok: false,
      message: `You can select at most ${MAX_ACCOUNTS_COUNT_TO_OPERATE} accounts.`,
    };
  }
  const maxAccounts = maxAccountsForUserCount(userCount);
  if (accountCount > maxAccounts) {
    const userLabel = `${userCount} ${pluralize(userCount, 'user', 'users')}`;
    const accountLabel = `${accountCount} ${pluralize(accountCount, 'account', 'accounts')}`;
    return {
      ok: false,
      message: `Max ${MAX_USER_COUNT_TO_OPERATE} operations allowed. ${userLabel} × ${accountLabel} = ${
        userCount * accountCount
      } operations. Reduce accounts or users.`,
    };
  }
  return { ok: true };
}

/**
 * Legacy (non-registry) permissions the bulk editor also offers (map ticket T8/T9). These are NOT
 * in the server descriptor list (`getActivePermissionDefinitions` — legacy perms are seeded
 * inactive by T1), so their labels/kinds/options are defined here on the client, mirroring what the
 * old full-form editor rendered. The server still accepts + grant-gates them (column-storage path),
 * so only the client-side picker metadata is needed.
 *
 * `bulkValueKind` tells the value picker + payload builder how to map the picked string back to the
 * value the server expects:
 *  - 'boolean' → Yes/No → true/false
 *  - 'nexa'    → No/Use/Enable → null/1/2
 *  - 'api'     → Hide/View/Edit → null/1/2
 */
export type BulkValueKind = 'boolean' | 'nexa' | 'api';

/**
 * The server's reason string for a profile that has no membership in the target account
 * (bulkPermissions.ts BulkPermissionState.notAMember). Kept in sync here so the client can detect
 * this specific skip and show the "invite them first" hint.
 */
export const NOT_A_MEMBER_REASON = 'Not a member of this account';

/** True if any account skipped a user because they aren't a member of that account. */
export function hasNotAMemberSkip(result: { perAccount?: { skipped: { reason?: string }[] }[] }) {
  return (result.perAccount ?? []).some((a) =>
    a.skipped.some((s) => s.reason === NOT_A_MEMBER_REASON),
  );
}

export interface LegacyPermissionDescriptor extends PermissionDescriptor {
  bulkValueKind: BulkValueKind;
}

const NEXA_OPTIONS = [
  { value: String(NEXA_PERMISSION.NO), label: 'No' },
  { value: String(NEXA_PERMISSION.USAGE), label: 'Use' },
  { value: String(NEXA_PERMISSION.ADMIN), label: 'Enable' },
];

const API_OPTIONS = [
  // Hide is the "null" (no access) state; use a stable sentinel string for the picker.
  { value: 'null', label: 'Hide' },
  { value: String(API_MANAGEMENT_PERMISSION.VIEW), label: 'View' },
  { value: String(API_MANAGEMENT_PERMISSION.EDIT), label: 'Edit' },
];

/**
 * The legacy descriptors, built from the same label sources the old editor used. First-level
 * booleans render as Yes/No; Nexa + API Management render as enums.
 *
 * Audience (`ciEnabled`) and its 4 CI sub-permissions are intentionally EXCLUDED from the bulk
 * editor: in single-user edit they use an enable-then-nested-dropdown interaction that doesn't fit
 * the one-permission-per-op bulk model, and the permission is infrequently used. Set those via the
 * single-user edit flow instead.
 */
export function getLegacyPermissionDescriptors(): LegacyPermissionDescriptor[] {
  const booleans: LegacyPermissionDescriptor[] = [
    ...Object.entries(FISRT_LEVEL_PERMISSION_INFO)
      // exclude Audience (ciEnabled) — see note above
      .filter(([key]) => key !== 'ciEnabled')
      .map(([key, label]) => ({
        key,
        label: label as string,
        kind: 'boolean' as const,
        default: false,
        storage: { kind: 'column' as const },
        grantRule: 'any-admin',
        bulkValueKind: 'boolean' as const,
      })),
  ];
  const enums: LegacyPermissionDescriptor[] = [
    {
      key: 'nexaPermission',
      label: 'Nexa Permission',
      kind: 'enum',
      options: NEXA_OPTIONS,
      default: String(NEXA_PERMISSION.USAGE),
      storage: { kind: 'column' },
      grantRule: 'any-admin',
      bulkValueKind: 'nexa',
    },
    {
      key: 'apiManagementPermission',
      label: 'API Management',
      kind: 'enum',
      options: API_OPTIONS,
      default: 'null',
      storage: { kind: 'column' },
      grantRule: 'any-admin',
      bulkValueKind: 'api',
    },
  ];
  return [...booleans, ...enums];
}

/**
 * Map a picked string value back to what the server's `set` expects for this permission. KV/active
 * descriptors coerce booleans from 'true'/'false' and pass enum strings through; legacy Nexa/API
 * enums map to their numeric levels (or null for the no-access option).
 */
export function toBulkSetValue(
  bulkValueKind: BulkValueKind | undefined,
  isEnum: boolean,
  raw: string,
): boolean | number | string | null {
  if (bulkValueKind === 'nexa' || bulkValueKind === 'api') {
    // 'null' sentinel → null (no access / hide); otherwise the numeric level.
    return raw === 'null' ? null : Number(raw);
  }
  if (bulkValueKind === 'boolean' || !isEnum) {
    return raw === 'true';
  }
  // active enum descriptor (e.g. a future registry enum) — server validates the string value.
  return raw;
}

/**
 * Build the result report from the server's per-account rollup (map ticket T8/T9), showing per
 * account BOTH what succeeded and what didn't — each with the user EMAILs and the account — not bare
 * ids or a lone count. The operator may not remember which users/accounts they picked, so the
 * report is self-describing.
 *
 * The server returns `updated` as a COUNT (not a user list), so the succeeded users are derived:
 * for each account, succeeded = (submitted userIds) − skipped − failed. The buckets partition the
 * submitted set exactly (server: each user is updated XOR skipped XOR failed), so this is exact. As
 * a guard, if the derived count ≠ the server's `updated` count we fall back to a plain count line so
 * names are never shown wrong.
 *
 * `submittedUserIds` = the ids applied to every account (this UI sends the same selection to each).
 * The server can't resolve an email for the "not a member" skip (no profile in that account), so
 * email labels fall back to the client's id->email map for the selected users.
 */
export function bulkResultToAccountStatusInfo(
  result: BulkPermissionsResult,
  emailByUserId: Map<number, string>,
  submittedUserIds: number[] = [],
): OperationStatusInfo[] {
  const out: OperationStatusInfo[] = [];
  const labelFor = (row: { id: number; email?: string }) =>
    row.email ?? emailByUserId.get(row.id) ?? String(row.id);
  const emailForId = (id: number) => emailByUserId.get(id) ?? String(id);

  for (const account of result.perAccount ?? []) {
    const accountName = account.name ?? `account ${account.id}`;

    // Succeeded = submitted − skipped − failed (derived; server only gives a count).
    const notSucceeded = new Set<number>([
      ...account.skipped.map((r) => r.id),
      ...account.failed.map((r) => r.id),
    ]);
    const succeededIds = submittedUserIds.filter((id) => !notSucceeded.has(id));
    if (account.updated > 0) {
      if (succeededIds.length === account.updated) {
        out.push({
          state: 'success',
          title: `${accountName} — ${succeededIds.map(emailForId).join(', ')}`,
          content: 'Permissions applied',
        });
      } else {
        // Derived list disagrees with the server count → show the trustworthy count instead.
        out.push({
          state: 'success',
          title: `${accountName} — ${account.updated} user${account.updated === 1 ? '' : 's'} updated`,
          content: 'Permissions applied',
        });
      }
    }

    // Skipped/failed, grouped by reason within the account.
    const rows = [
      ...account.skipped.map((r) => ({ ...r, kind: 'Skipped' as const })),
      ...account.failed.map((r) => ({ ...r, kind: 'Failed' as const })),
    ];
    const byReason = new Map<string, string[]>();
    for (const row of rows) {
      const reason = `${row.kind}: ${row.reason ?? row.kind}`;
      const list = byReason.get(reason) ?? [];
      list.push(labelFor(row));
      byReason.set(reason, list);
    }
    for (const [reason, users] of byReason.entries()) {
      out.push({
        state: 'fail',
        title: `${accountName} — ${users.join(', ')}`,
        content: reason,
      });
    }
  }
  return out;
}
